import { useEffect } from "react";

import classnames from "classnames";
import { Field, connect } from "formik";
import get from "lodash/get";
import PropTypes from "prop-types";

import { MaskPIIInfo } from "hooks/useHidePIIInfo/MaskPIIInfo";

import Text from "../Text";

import styles from "./Input.module.scss";

function Input({
  className,
  label,
  labelDisabled,
  placeholder,
  helpText,
  required,
  disabled,
  readOnly,
  validate,
  type,
  pattern,
  name,
  formik,
  onTouched,
  theme,
  inputIcon,
  dataTestId,
  masked,
  endButton,
  ...props
}) {
  const error = get(formik.errors, name);
  const touched = get(formik.touched, name);

  useEffect(() => {
    if (!onTouched) return;
    if (touched) {
      onTouched();
    }
  }, [touched, onTouched]);

  const labelComponent = (
    <label
      className={classnames(styles.label, {
        required: required && label,
        transparent: !label,
      })}
      htmlFor={name}
    >
      {label}
    </label>
  );

  return (
    <Field name={name}>
      {({ field }) => {
        const { onChange, value } = field;

        return (
          <div
            className={classnames(styles.container, styles[theme], className)}
          >
            {labelDisabled ? null : labelComponent}
            <div
              className={classnames(styles["input-container"], {
                [styles["no-label"]]: labelDisabled,
              })}
            >
              <input
                {...props}
                id={name}
                className={classnames(
                  styles.input,
                  disabled && styles.disabled,
                  {
                    [styles["input-error"]]: touched && error,
                  },
                )}
                type={type}
                required={required}
                name={field.name}
                value={masked ? MaskPIIInfo(value) : value || ""}
                pattern={pattern}
                onBlur={field.onBlur}
                onChange={e => {
                  if (disabled) return;
                  if (validate) {
                    if (validate(e.target.value)) {
                      onChange(e);
                    }
                  } else {
                    onChange(e);
                  }
                }}
                placeholder={placeholder}
                disabled={disabled}
                readOnly={readOnly}
              />
              {endButton ?? null}
              {inputIcon && (
                <div className={styles["input-icon"]}>{inputIcon}</div>
              )}
            </div>
            {touched && error ? (
              <div
                className={styles["error-container"]}
                data-testid={`error-${dataTestId}`}
              >
                {error}
              </div>
            ) : null}
            {helpText ? (
              <div
                className={classnames(styles["help-text-container"], {
                  [styles["input-error"]]: touched && error,
                })}
              >
                {helpText ? (
                  <Text types={["regular", "sm", "grey-2"]}>{helpText}</Text>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      }}
    </Field>
  );
}

Input.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  labelDisabled: PropTypes.bool,
  placeholder: PropTypes.string,
  helpText: PropTypes.string || PropTypes.node,
  type: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  name: PropTypes.string.isRequired,
  validate: PropTypes.func,
  formik: PropTypes.shape({
    errors: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired,
  }).isRequired,
  onTouched: PropTypes.func,
  theme: PropTypes.oneOf(["normal", "checkout", "popup"]),
  pattern: PropTypes.string,
  dataTestId: PropTypes.string,
  masked: PropTypes.bool,
};

Input.defaultProps = {
  label: null,
  labelDisabled: false,
  placeholder: "",
  validate: null,
  helpText: null,
  type: "text",
  required: false,
  disabled: false,
  readOnly: false,
  onTouched: null,
  theme: "normal",
  inputIcon: null,
  dataTestId: null,
  masked: false,
};

Input.styles = styles;

export default connect(Input);
